'use client';

import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DataGrid, { Column, Summary, TotalItem, Export, SearchPanel, Scrolling } from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';

import TimeTaxSupportSystemClient from '../../../system';
import MessageCourier from '../../../lib/MessageCourier';
import { User } from '../../../system/User';
import { IReporteHorasCumplimientoGeneral, IReporteHorasCumplimientoIndividual } from '../../../system/reporting/ReportingSystem';
import { IorganizationLevels } from '../../../system/AuthenticationSystem';
import { EscalableExporterOptions, exportToExcel } from '../../../lib/Util';
import useIsMobile from '../../../common/custom-hooks/useIsMobile';

/* =======================
   TIPOS INTERNOS
======================= */

interface IUnifiedRow {
  hierarchy_path: string;
  cant_colaboradores: number;
  horas_cargadas_totales: number;
  horas_esperadas_totales: number;
  porcentaje_cumplimiento_general: number;
  cant_colaboradores_horas_completas: number;
  cant_colaboradores_horas_incompletas: number;
  porcentaje_cumplimiento_individual: number;
}

interface Props {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (state: boolean) => void;
  user: User;
  itemsGeneral: IReporteHorasCumplimientoGeneral[];
  itemsIndividual: IReporteHorasCumplimientoIndividual[];
  from: DateTime;
  to: DateTime;
  resetTableAfterExport: () => void;
  levelsN: IorganizationLevels[];
}

/* =======================
   COMPONENTE
======================= */

export const ReportHoursSectorCumplimientoUnificado = (props: Props) => {
  const { t } = useTranslation();

  /* =======================
     UNIFICAR DATA
  ======================= */

  const dataSource: IUnifiedRow[] = useMemo(() => {
    const map = new Map<string, IUnifiedRow>();

    props.itemsGeneral.forEach((g) => {
      map.set(g.hierarchy_path, {
        hierarchy_path: g.hierarchy_path,
        cant_colaboradores: g.cant_colaboradores,
        horas_cargadas_totales: g.horas_cargadas_totales,
        horas_esperadas_totales: g.horas_esperadas_totales,
        porcentaje_cumplimiento_general: g.porcentaje_cumplimiento,
        cant_colaboradores_horas_completas: 0,
        cant_colaboradores_horas_incompletas: 0,
        porcentaje_cumplimiento_individual: 0,
      });
    });

    props.itemsIndividual.forEach((i) => {
      const row = map.get(i.hierarchy_path);

      if (row) {
        row.cant_colaboradores_horas_completas = i.cant_colaboradores_horas_completas;
        row.cant_colaboradores_horas_incompletas = i.cant_colaboradores_horas_incompletas;
        row.porcentaje_cumplimiento_individual = i.porcentaje_cumplimiento;
      }
    });

    return Array.from(map.values());
  }, [props.itemsGeneral, props.itemsIndividual, props.levelsN]);

  /* =======================
     EXPORT
  ======================= */

  const onExporting = async (e: any) => {
    const options: EscalableExporterOptions = {
      fileName: `cumplimiento-sector-${props.from.toFormat('dd-LL-yyyy')}-a-${props.to.toFormat('dd-LL-yyyy')}`,
      sheetName: t('Cumplimiento por sector'),
    };

    await exportToExcel(e, options);
    props.resetTableAfterExport();
  };

  /* =======================
     RENDER
  ======================= */

  return (
    <>
      <Button
        className="btn-dinamic-table"
        icon="help"
        hint={t(
          'Si la cantidad de colaboradores es mayor a la esperada, probablemente se debe a colaboradores con más de un cargo. Los valores son relativos a la cantidad de cargos distintos.',
        )}
      />

      <DataGrid dataSource={dataSource} keyExpr="hierarchy_path" showBorders columnAutoWidth wordWrapEnabled height={620} onExporting={onExporting}>
        <SearchPanel visible placeholder={t('Buscar...')} />
        <Scrolling mode="virtual" />
        <Export enabled />

        {/* ÁREA */}
        <Column dataField={'hierarchy_path'} caption={t('Area')} width={580} />

        {/* ===== GENERAL ===== */}
        <Column caption={t('Cumplimiento general por sector')}>
          <Column dataField="cant_colaboradores" caption={t('# Colaboradores')} />
          <Column dataField="horas_cargadas_totales" caption={t('Horas confirmadas')} />
          <Column dataField="horas_esperadas_totales" caption={t('Horas esperadas')} />
          <Column dataField="porcentaje_cumplimiento_general" caption={t('% de cumplimiento')} customizeText={(e) => e.value != null ? `${Number(e.value).toFixed(2)}%` : ''} />
        </Column>

        {/* ===== INDIVIDUAL ===== */}
        <Column caption={t('Cumplimiento individual por sector')}>
          <Column dataField="cant_colaboradores_horas_completas" caption={t('# Completaron registro')} />
          <Column dataField="cant_colaboradores_horas_incompletas" caption={t('# No completaron registro')} />
          <Column dataField="porcentaje_cumplimiento_individual" caption={t('% de cumplimiento')} customizeText={(e) => e.value != null ? `${Number(e.value).toFixed(2)}%` : ''} />
        </Column>

        <Summary>
          {/* ===== GENERAL ===== */}
          <TotalItem column="cant_colaboradores" summaryType="sum" customizeText={(e) => e.value} />
          <TotalItem column="horas_cargadas_totales" summaryType="sum" customizeText={(e) => e.value} />
          <TotalItem column="horas_esperadas_totales" summaryType="sum" customizeText={(e) => e.value} />

          {/* ===== INDIVIDUAL ===== */}
          <TotalItem column="cant_colaboradores_horas_completas" summaryType="sum" customizeText={(e) => e.value} />
          <TotalItem column="cant_colaboradores_horas_incompletas" summaryType="sum" customizeText={(e) => e.value} />

          {/* ===== PROMEDIOS DE % ===== */}
          <TotalItem column="porcentaje_cumplimiento_general" summaryType="avg"   customizeText={(e) => e.value != null ? `${Number(e.value).toFixed(2)}%` : ''} />
          <TotalItem column="porcentaje_cumplimiento_individual" summaryType="avg"   customizeText={(e) => e.value != null ? `${Number(e.value).toFixed(2)}%` : ''} />
        </Summary>
      </DataGrid>
    </>
  );
};
